import React,{ forwardRef, useMemo } from 'react';
import { ResponsivePie } from '@nivo/pie';
import { OrdinalColorScaleConfig } from '@nivo/colors';
import { Box } from '@mui/material';
import { ChartDataItem, ChartColorConfig } from '../types';

interface CenteredMetricProps {
    centerX: number;
    centerY: number;
    value: number;
    label?: string;
}

const CenteredMetric = ({ centerX, centerY, value, label }: CenteredMetricProps) => (
    <>
        <text
            x={centerX}
            y={label ? centerY - 10 : centerY}
            textAnchor="middle"
            dominantBaseline="central"
            style={{
                fontSize: '24px',
                fontWeight: 600,
            }}
        >
            {value}%
        </text>
        {label && (
            <text
                x={centerX}
                y={centerY + 20}
                textAnchor="middle"
                dominantBaseline="central"
                style={{
                    fontSize: '0.875rem',
                    fill: '#666',
                }}
            >
                {label}
            </text>
        )}
    </>
);

interface DonutChartProps {
    data: ChartDataItem[];
    percentage: number;
    label?: string;
    colors?: ChartColorConfig;
    height?: string | number;
    interactive?: boolean;
}

export const DonutChart = forwardRef<HTMLDivElement, DonutChartProps>(
    (
        {
            data,
            percentage,
            label,
            colors = { scheme: 'nivo' },
            height = '100%',
            interactive = false,
        },
        ref
    ) => {
        const layers = useMemo(
            () => [
                'arcs',
                'arcLabels',
                'arcLinkLabels',
                'legends',
                (props: { centerX: number; centerY: number }) => (
                    <CenteredMetric
                        key="centered-metric"
                        centerX={props.centerX}
                        centerY={props.centerY}
                        value={percentage}
                        label={label}
                    />
                ),
            ],
            [percentage, label]
        );

        const colorConfig = useMemo((): OrdinalColorScaleConfig => {
            if (colors.scheme) {
                return { scheme: colors.scheme } as OrdinalColorScaleConfig;
            }
            if (colors.datum) {
                return { datum: colors.datum } as OrdinalColorScaleConfig;
            }
            return { scheme: 'nivo' } as OrdinalColorScaleConfig;
        }, [colors]);

        return (
            <Box ref={ref} sx={{ height, width: '100%' }}>
                <ResponsivePie
                    data={data}
                    margin={{ top: 5, right: 0, bottom: 5, left: 0 }}
                    innerRadius={0.8}
                    padAngle={0.7}
                    cornerRadius={1}
                    activeOuterRadiusOffset={10}
                    borderWidth={1}
                    borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                    colors={colorConfig}
                    enableArcLinkLabels={false}
                    enableArcLabels={false}
                    isInteractive={interactive}
                    layers={layers}
                    animate
                    motionConfig="gentle"
                />
            </Box>
        );
    }
);

DonutChart.displayName = 'DonutChart';
